import { useState, useEffect } from "react";
import { Settings, Printer, Download, ExternalLink, BookOpen, CheckCircle, Percent, Save, Utensils, Package, AlertTriangle, Globe, QrCode, Truck, Store, Copy, ExternalLink as LinkIcon, Smartphone, MessageSquare, Bell, XCircle, Tag, PackageX, UtensilsCrossed, ClipboardList, Ticket, Users, Receipt, Keyboard, Zap, Tablet, KeyRound, Monitor } from "lucide-react";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useServiceTax } from "@/hooks/useServiceTax";
import { useCouvert } from "@/hooks/useCouvert";
import { useStockSettings } from "@/hooks/useStockSettings";
import { usePublicMenuSettings } from "@/hooks/usePublicMenuSettings";
import { useTables } from "@/hooks/useTables";
import { useWhatsAppNotificationSettings } from "@/hooks/useWhatsAppNotificationSettings";
import { usePeopleCountSettings } from "@/hooks/usePeopleCountSettings";
import { useRequireCommandEntry } from "@/hooks/useRequireCommandEntry";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QRCodeGenerator } from "@/components/public/QRCodeGenerator";
import { PWAInstructions } from "@/components/pwa/PWAInstructions";
import { TabletInstallInstructions } from "@/components/tablet/TabletInstallInstructions";
import { TabletBannersManager } from "@/components/tablet/TabletBannersManager";
import { TableManagementCard } from "@/components/settings/TableManagementCard";
import { CommandManagementCard } from "@/components/settings/CommandManagementCard";
import { EntryTypesCard } from "@/components/settings/EntryTypesCard";
import { QZTrayConfigCard } from "@/components/settings/QZTrayConfigCard";
import { TableConsumptionSettingsCard } from "@/components/settings/TableConsumptionSettingsCard";
import { PrintTemplateSettings } from "@/components/settings/PrintTemplateSettings";
import { CancellationPrintSettingsCard } from "@/components/settings/CancellationPrintSettingsCard";
import { ExitReleasePrintCard } from "@/components/settings/ExitReleasePrintCard";
import { AutoClosePeriodCard } from "@/components/settings/AutoClosePeriodCard";
import { TransferPendingConsumptionCard } from "@/components/settings/TransferPendingConsumptionCard";
import { PeriodTotalsVisibilityCard } from "@/components/settings/PeriodTotalsVisibilityCard";
import { BackupCard } from "@/components/settings/BackupCard";
import { DeliveryAutoReplyCard } from "@/components/settings/DeliveryAutoReplyCard";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePDVOperatorSettings } from "@/hooks/usePDVOperatorSettings";

function OperatorLoginCard() {
  const { requireOperatorLogin, updateRequireOperatorLogin } = usePDVOperatorSettings();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          Identificação de Operador no PDV
        </CardTitle>
        <CardDescription>
          Exige que o garçom/operador se identifique com PIN antes de cada pedido
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-base">Exigir login por PIN</Label>
            <p className="text-sm text-muted-foreground">
              Após enviar um pedido, o PDV volta para a tela de identificação
            </p>
          </div>
          <Switch
            checked={requireOperatorLogin}
            onCheckedChange={updateRequireOperatorLogin}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Configure o PIN de cada funcionário em Equipe → Editar funcionário
        </p>
      </CardContent>
    </Card>
  );
}

export default function ConfiguracoesPage() {
  const { activeRestaurant } = useRestaurantContext();
  const { serviceTaxPercentage, serviceTaxEnabled, updateServiceTax, isUpdating } = useServiceTax();
  const { couvertEnabled, couvertPrice, updateCouvert, isUpdating: isUpdatingCouvert } = useCouvert();
  const { stockBlockSales, lowMarginAlertPercentage, updateSettings, isUpdating: isUpdatingStock } = useStockSettings();
  const { settings: publicMenuSettings, updateSettings: updatePublicMenuSettings, isUpdating: isUpdatingPublicMenu } = usePublicMenuSettings();
  const { tables } = useTables();
  const { settings: notificationSettings, updateSettings: updateNotificationSettings, isUpdating: isUpdatingNotifications } = useWhatsAppNotificationSettings();
  
  const [taxEnabled, setTaxEnabled] = useState(serviceTaxEnabled);
  const [taxPercentage, setTaxPercentage] = useState(String(serviceTaxPercentage));
  const [hasChanges, setHasChanges] = useState(false);

  const [couvertEnabledState, setCouvertEnabledState] = useState(couvertEnabled);
  const [couvertPriceState, setCouvertPriceState] = useState(String(couvertPrice));
  const [hasCouvertChanges, setHasCouvertChanges] = useState(false);

  const [blockSales, setBlockSales] = useState(stockBlockSales);
  const [marginAlert, setMarginAlert] = useState(String(lowMarginAlertPercentage));
  const [hasStockChanges, setHasStockChanges] = useState(false);

  // Public menu settings state
  const [publicMenuEnabled, setPublicMenuEnabled] = useState(publicMenuSettings.publicMenuEnabled);
  const [customerOrderingEnabled, setCustomerOrderingEnabled] = useState(publicMenuSettings.customerOrderingEnabled);
  const [deliveryEnabled, setDeliveryEnabled] = useState(publicMenuSettings.deliveryEnabled);
  const [pickupEnabled, setPickupEnabled] = useState(publicMenuSettings.pickupEnabled);
  const [deliveryFee, setDeliveryFee] = useState(String(publicMenuSettings.deliveryFee));
  const [minOrderAmount, setMinOrderAmount] = useState(String(publicMenuSettings.minOrderAmount));
  const [deliveryTimeEstimate, setDeliveryTimeEstimate] = useState(publicMenuSettings.deliveryTimeEstimate);
  const [autoApproveTableOrders, setAutoApproveTableOrders] = useState(publicMenuSettings.autoApproveTableOrders);
  const [hasPublicMenuChanges, setHasPublicMenuChanges] = useState(false);

  // WhatsApp notification settings state
  const [whatsappNumber, setWhatsappNumber] = useState(notificationSettings.whatsappNumber || "");
  const [notifyDiscount, setNotifyDiscount] = useState(notificationSettings.notifyDiscount);
  const [notifyCancellation, setNotifyCancellation] = useState(notificationSettings.notifyCancellation);
  const [notifyLowStock, setNotifyLowStock] = useState(notificationSettings.notifyLowStock);
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);

  useEffect(() => {
    setTaxEnabled(serviceTaxEnabled);
    setTaxPercentage(String(serviceTaxPercentage));
  }, [serviceTaxEnabled, serviceTaxPercentage]);

  useEffect(() => {
    setCouvertEnabledState(couvertEnabled);
    setCouvertPriceState(String(couvertPrice));
  }, [couvertEnabled, couvertPrice]);

  useEffect(() => {
    setBlockSales(stockBlockSales);
    setMarginAlert(String(lowMarginAlertPercentage));
  }, [stockBlockSales, lowMarginAlertPercentage]);

  useEffect(() => {
    setPublicMenuEnabled(publicMenuSettings.publicMenuEnabled);
    setCustomerOrderingEnabled(publicMenuSettings.customerOrderingEnabled);
    setDeliveryEnabled(publicMenuSettings.deliveryEnabled);
    setPickupEnabled(publicMenuSettings.pickupEnabled);
    setDeliveryFee(String(publicMenuSettings.deliveryFee));
    setMinOrderAmount(String(publicMenuSettings.minOrderAmount));
    setDeliveryTimeEstimate(publicMenuSettings.deliveryTimeEstimate);
    setAutoApproveTableOrders(publicMenuSettings.autoApproveTableOrders);
  }, [publicMenuSettings]);

  useEffect(() => {
    setWhatsappNumber(notificationSettings.whatsappNumber || "");
    setNotifyDiscount(notificationSettings.notifyDiscount);
    setNotifyCancellation(notificationSettings.notifyCancellation);
    setNotifyLowStock(notificationSettings.notifyLowStock);
  }, [notificationSettings]);

  useEffect(() => {
    const percentageChanged = parseFloat(taxPercentage) !== serviceTaxPercentage;
    const enabledChanged = taxEnabled !== serviceTaxEnabled;
    setHasChanges(percentageChanged || enabledChanged);
  }, [taxEnabled, taxPercentage, serviceTaxEnabled, serviceTaxPercentage]);

  useEffect(() => {
    const priceChanged = parseFloat(couvertPriceState) !== couvertPrice;
    const enabledChanged = couvertEnabledState !== couvertEnabled;
    setHasCouvertChanges(priceChanged || enabledChanged);
  }, [couvertEnabledState, couvertPriceState, couvertEnabled, couvertPrice]);

  useEffect(() => {
    const blockChanged = blockSales !== stockBlockSales;
    const marginChanged = parseFloat(marginAlert) !== lowMarginAlertPercentage;
    setHasStockChanges(blockChanged || marginChanged);
  }, [blockSales, marginAlert, stockBlockSales, lowMarginAlertPercentage]);

  useEffect(() => {
    const changed = 
      publicMenuEnabled !== publicMenuSettings.publicMenuEnabled ||
      customerOrderingEnabled !== publicMenuSettings.customerOrderingEnabled ||
      deliveryEnabled !== publicMenuSettings.deliveryEnabled ||
      pickupEnabled !== publicMenuSettings.pickupEnabled ||
      parseFloat(deliveryFee) !== publicMenuSettings.deliveryFee ||
      parseFloat(minOrderAmount) !== publicMenuSettings.minOrderAmount ||
      deliveryTimeEstimate !== publicMenuSettings.deliveryTimeEstimate ||
      autoApproveTableOrders !== publicMenuSettings.autoApproveTableOrders;
    setHasPublicMenuChanges(changed);
  }, [publicMenuEnabled, customerOrderingEnabled, deliveryEnabled, pickupEnabled, deliveryFee, minOrderAmount, deliveryTimeEstimate, autoApproveTableOrders, publicMenuSettings]);

  useEffect(() => {
    const changed = 
      whatsappNumber !== (notificationSettings.whatsappNumber || "") ||
      notifyDiscount !== notificationSettings.notifyDiscount ||
      notifyCancellation !== notificationSettings.notifyCancellation ||
      notifyLowStock !== notificationSettings.notifyLowStock;
    setHasNotificationChanges(changed);
  }, [whatsappNumber, notifyDiscount, notifyCancellation, notifyLowStock, notificationSettings]);

  const handleSaveServiceTax = async () => {
    await updateServiceTax(parseFloat(taxPercentage) || 0, taxEnabled);
    setHasChanges(false);
  };

  const handleSaveCouvert = async () => {
    await updateCouvert(couvertEnabledState, parseFloat(couvertPriceState) || 0);
    setHasCouvertChanges(false);
  };

  const handleSaveStockSettings = async () => {
    const success = await updateSettings({
      stockBlockSales: blockSales,
      lowMarginAlertPercentage: parseFloat(marginAlert) || 30,
    });
    if (success) {
      toast.success("Configurações de estoque salvas!");
      setHasStockChanges(false);
    } else {
      toast.error("Erro ao salvar configurações");
    }
  };

  const handleSavePublicMenuSettings = async () => {
    await updatePublicMenuSettings({
      publicMenuEnabled,
      customerOrderingEnabled,
      deliveryEnabled,
      pickupEnabled,
      deliveryFee: parseFloat(deliveryFee) || 0,
      minOrderAmount: parseFloat(minOrderAmount) || 0,
      deliveryTimeEstimate,
      autoApproveTableOrders,
    });
    setHasPublicMenuChanges(false);
  };

  const handleSaveNotificationSettings = async () => {
    await updateNotificationSettings({
      whatsappNumber: whatsappNumber || null,
      notifyDiscount,
      notifyCancellation,
      notifyLowStock,
    });
    setHasNotificationChanges(false);
  };

  const menuUrl = `${window.location.origin}/menu/${activeRestaurant?.slug}`;

  const copyMenuLink = () => {
    navigator.clipboard.writeText(menuUrl);
    toast.success("Link copiado!");
  };

  const activeTables = tables.filter(t => t.is_active);
  
  const { peopleCountCheckoutEnabled, updateSetting: updatePeopleCountSetting, isUpdating: isUpdatingPeopleCount } = usePeopleCountSettings();
  const { requireCommandEntry, updateSetting: updateRequireCommandEntry, isUpdating: isUpdatingCommandEntry } = useRequireCommandEntry();

  // Command Entry Requirement Card Component
  const CommandEntryRequirementCard = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Abertura Prévia de Comandas
        </CardTitle>
        <CardDescription>
          Exigir abertura de comanda com tipo de entrada antes de permitir lançamentos
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-base">Ativar Abertura Prévia Obrigatória</Label>
            <p className="text-sm text-muted-foreground">
              Quando ativado, só permite lançar pedidos em comandas que foram abertas com um tipo de entrada
            </p>
          </div>
          <Switch
            checked={requireCommandEntry}
            onCheckedChange={updateRequireCommandEntry}
            disabled={isUpdatingCommandEntry}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Acesse "Abertura de Comandas" no menu lateral para vincular entradas às comandas do dia.
        </p>
      </CardContent>
    </Card>
  );

  // People Count Checkout Card Component
  const PeopleCountCheckoutCard = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          Confirmação de Pessoas no Checkout
        </CardTitle>
        <CardDescription>
          Exibir campo para confirmar/ajustar o número de pessoas ao fechar a conta
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-base">Ativar Confirmação de Pessoas</Label>
            <p className="text-sm text-muted-foreground">
              Mostra um campo para confirmar ou ajustar o número de pessoas no momento do checkout
            </p>
          </div>
          <Switch
            checked={peopleCountCheckoutEnabled}
            onCheckedChange={updatePeopleCountSetting}
            disabled={isUpdatingPeopleCount}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          O número de pessoas é utilizado para calcular o ticket médio por pessoa nos relatórios.
        </p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
          <Settings className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Configurações</h1>
          <p className="text-sm text-muted-foreground">Gerencie as configurações do restaurante</p>
        </div>
      </div>

      <Tabs defaultValue="geral" className="max-w-4xl">
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="geral">Geral</TabsTrigger>
          <TabsTrigger value="mesas-comandas">Mesas</TabsTrigger>
          <TabsTrigger value="entradas">Entradas</TabsTrigger>
          <TabsTrigger value="notificacoes">Notificações</TabsTrigger>
          <TabsTrigger value="cardapio">Cardápio</TabsTrigger>
          <TabsTrigger value="qrcodes">QR Codes</TabsTrigger>
          <TabsTrigger value="garcom">Garçom</TabsTrigger>
          <TabsTrigger value="tablet">Tablet</TabsTrigger>
          <TabsTrigger value="impressao">Impressão</TabsTrigger>
        </TabsList>

        {/* Tab Geral */}
        <TabsContent value="geral" className="space-y-6 mt-6">
          {/* Dados do Restaurante */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dados do Restaurante</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-sm text-muted-foreground">Nome:</span>
                <p className="font-medium">{activeRestaurant?.name}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Slug:</span>
                <p className="font-medium">{activeRestaurant?.slug}</p>
              </div>
              {activeRestaurant?.phone && (
                <div>
                  <span className="text-sm text-muted-foreground">Telefone:</span>
                  <p className="font-medium">{activeRestaurant.phone}</p>
                </div>
              )}
              {activeRestaurant?.address && (
                <div>
                  <span className="text-sm text-muted-foreground">Endereço:</span>
                  <p className="font-medium">{activeRestaurant.address}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* KDS - Painel da Cozinha */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                KDS - Painel da Cozinha
              </CardTitle>
              <CardDescription>
                Abra este link em uma TV ou tablet na cozinha para exibir os pedidos em tempo real
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg border p-3 bg-muted/50">
                <code className="flex-1 text-sm break-all">{window.location.origin}/kds</code>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/kds`);
                    toast.success("Link do KDS copiado!");
                  }}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copiar Link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open("/kds", "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Abrir KDS
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Identificação de Operador no PDV */}
          <OperatorLoginCard />

          {/* Fechamento Automático de Período */}
          <AutoClosePeriodCard />

          {/* Transferir Movimento Pendente */}
          <TransferPendingConsumptionCard />

          {/* Visibilidade do Faturamento no Fechamento */}
          <PeriodTotalsVisibilityCard />

          {/* Controle de Estoque */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Controle de Estoque
              </CardTitle>
              <CardDescription>
                Configure como o sistema trata produtos sem estoque
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-base">Bloquear Vendas Sem Estoque</Label>
                  <p className="text-sm text-muted-foreground">
                    Impede a venda de produtos com ingredientes insuficientes
                  </p>
                </div>
                <Switch
                  checked={blockSales}
                  onCheckedChange={setBlockSales}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="marginAlert" className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  Alerta de Margem Baixa (%)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="marginAlert"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={marginAlert}
                    onChange={(e) => setMarginAlert(e.target.value)}
                    className="w-32"
                  />
                  <span className="flex items-center text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Produtos com margem abaixo deste valor serão destacados nos relatórios
                </p>
              </div>

              {hasStockChanges && (
                <Button onClick={handleSaveStockSettings} disabled={isUpdatingStock} className="gap-2">
                  <Save className="h-4 w-4" />
                  {isUpdatingStock ? "Salvando..." : "Salvar Alterações"}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Taxa de Serviço */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Percent className="h-5 w-5" />
                Taxa de Serviço
              </CardTitle>
              <CardDescription>
                Configure a taxa de serviço que será aplicada automaticamente nos pedidos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-base">Ativar Taxa de Serviço</Label>
                  <p className="text-sm text-muted-foreground">
                    A taxa será aplicada automaticamente no checkout
                  </p>
                </div>
                <Switch
                  checked={taxEnabled}
                  onCheckedChange={setTaxEnabled}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="taxPercentage">Porcentagem (%)</Label>
                <div className="flex gap-2">
                  <Input
                    id="taxPercentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={taxPercentage}
                    onChange={(e) => setTaxPercentage(e.target.value)}
                    className="w-32"
                    disabled={!taxEnabled}
                  />
                  <span className="flex items-center text-muted-foreground">%</span>
                </div>
              </div>

              {hasChanges && (
                <Button onClick={handleSaveServiceTax} disabled={isUpdating} className="gap-2">
                  <Save className="h-4 w-4" />
                  {isUpdating ? "Salvando..." : "Salvar Alterações"}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Couvert */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Utensils className="h-5 w-5" />
                Couvert / Taxa Adicional
              </CardTitle>
              <CardDescription>
                Configure o couvert ou taxa adicional por pessoa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-base">Ativar Couvert</Label>
                  <p className="text-sm text-muted-foreground">
                    O couvert será cobrado por pessoa no checkout
                  </p>
                </div>
                <Switch
                  checked={couvertEnabledState}
                  onCheckedChange={setCouvertEnabledState}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="couvertPrice">Valor por pessoa (R$)</Label>
                <div className="flex gap-2">
                  <Input
                    id="couvertPrice"
                    type="number"
                    min="0"
                    step="0.50"
                    value={couvertPriceState}
                    onChange={(e) => setCouvertPriceState(e.target.value)}
                    className="w-32"
                    disabled={!couvertEnabledState}
                  />
                </div>
              </div>

              {hasCouvertChanges && (
                <Button onClick={handleSaveCouvert} disabled={isUpdatingCouvert} className="gap-2">
                  <Save className="h-4 w-4" />
                  {isUpdatingCouvert ? "Salvando..." : "Salvar Alterações"}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Atalhos de Teclado PDV */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Keyboard className="h-5 w-5" />
                Atalhos de Teclado
              </CardTitle>
              <CardDescription>
                Exibir dicas de atalhos nos botões do PDV
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-base">Mostrar atalhos de teclado</Label>
                  <p className="text-sm text-muted-foreground">
                    Exibe Esc, Ctrl+Enter, F12 nos botões principais do PDV
                  </p>
                </div>
                <Switch
                  checked={Boolean(activeRestaurant?.show_pdv_keyboard_shortcuts)}
                  onCheckedChange={async (checked) => {
                    if (!activeRestaurant?.id) return;
                    const { error } = await supabase
                      .from("restaurants")
                      .update({ show_pdv_keyboard_shortcuts: checked })
                      .eq("id", activeRestaurant.id);
                    if (error) {
                      toast.error("Erro ao salvar configuração");
                    } else {
                      toast.success("Configuração salva");
                      window.location.reload();
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Pagamento Parcial */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Pagamento Parcial
              </CardTitle>
              <CardDescription>
                Permite que clientes paguem sua parte antes do fechamento total da mesa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-base">Habilitar Pagamento Parcial</Label>
                  <p className="text-sm text-muted-foreground">
                    Exibe o botão "Pagamento Parcial" na tela de checkout
                  </p>
                </div>
                <Switch
                  checked={Boolean(activeRestaurant?.partial_payment_enabled)}
                  onCheckedChange={async (checked) => {
                    if (!activeRestaurant?.id) return;
                    const { error } = await supabase
                      .from("restaurants")
                      .update({ partial_payment_enabled: checked })
                      .eq("id", activeRestaurant.id);
                    if (error) {
                      toast.error("Erro ao salvar configuração");
                    } else {
                      toast.success("Configuração salva");
                      window.location.reload();
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* People Count Checkout Setting */}
          <PeopleCountCheckoutCard />

          {/* Backup de Dados */}
          <BackupCard />
        </TabsContent>

        {/* Tab Tipos de Entrada */}
        <TabsContent value="entradas" className="space-y-6 mt-6">
          <EntryTypesCard />
        </TabsContent>

        {/* Tab Notificações WhatsApp */}
        <TabsContent value="notificacoes" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Notificações via WhatsApp
              </CardTitle>
              <CardDescription>
                Receba alertas no WhatsApp quando operações críticas acontecerem
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="whatsappNumber">Número do WhatsApp</Label>
                <div className="flex gap-2">
                  <Input
                    id="whatsappNumber"
                    type="tel"
                    placeholder="5511999999999"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    className="max-w-xs"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Digite o número com código do país (ex: 5511999999999)
                </p>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label className="text-base font-medium">Tipos de Notificação</Label>
                
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <Tag className="h-4 w-4 text-destructive" />
                    </div>
                    <div>
                      <Label className="text-base">Descontos Aplicados</Label>
                      <p className="text-sm text-muted-foreground">
                        Notificar quando um desconto for aplicado em um pedido
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notifyDiscount}
                    onCheckedChange={setNotifyDiscount}
                    disabled={!whatsappNumber}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <XCircle className="h-4 w-4 text-destructive" />
                    </div>
                    <div>
                      <Label className="text-base">Cancelamento de Pedidos</Label>
                      <p className="text-sm text-muted-foreground">
                        Notificar quando um pedido for cancelado
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notifyCancellation}
                    onCheckedChange={setNotifyCancellation}
                    disabled={!whatsappNumber}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-warning/10 flex items-center justify-center">
                      <PackageX className="h-4 w-4 text-warning" />
                    </div>
                    <div>
                      <Label className="text-base">Estoque Baixo</Label>
                      <p className="text-sm text-muted-foreground">
                        Notificar quando um ingrediente atingir o estoque mínimo
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notifyLowStock}
                    onCheckedChange={setNotifyLowStock}
                    disabled={!whatsappNumber}
                  />
                </div>
              </div>

              {!whatsappNumber && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                  <Bell className="h-4 w-4" />
                  <span>Configure um número de WhatsApp para ativar as notificações</span>
                </div>
              )}

              {hasNotificationChanges && (
                <Button onClick={handleSaveNotificationSettings} disabled={isUpdatingNotifications} className="gap-2">
                  <Save className="h-4 w-4" />
                  {isUpdatingNotifications ? "Salvando..." : "Salvar Alterações"}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Cardápio Digital */}
        <TabsContent value="cardapio" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Cardápio Digital Público
              </CardTitle>
              <CardDescription>
                Permita que clientes acessem seu cardápio online e façam pedidos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-base">Ativar Cardápio Público</Label>
                  <p className="text-sm text-muted-foreground">
                    Seu cardápio estará disponível em {menuUrl}
                  </p>
                </div>
                <Switch
                  checked={publicMenuEnabled}
                  onCheckedChange={setPublicMenuEnabled}
                />
              </div>

              {publicMenuEnabled && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Input value={menuUrl} readOnly className="flex-1 bg-background" />
                  <Button variant="outline" size="icon" onClick={copyMenuLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" asChild>
                    <a href={menuUrl} target="_blank" rel="noopener noreferrer">
                      <LinkIcon className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              )}

              <Separator />

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="text-base flex items-center gap-2">
                    <QrCode className="h-4 w-4" />
                    Pedidos via QR Code da Mesa
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Clientes podem fazer pedidos escaneando o QR Code na mesa
                  </p>
                </div>
                <Switch
                  checked={customerOrderingEnabled}
                  onCheckedChange={setCustomerOrderingEnabled}
                />
              </div>

              {customerOrderingEnabled && (
                <div className="ml-4 space-y-4 border-l-2 border-muted pl-4">
                  <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30">
                    <div>
                      <Label className="text-base flex items-center gap-2">
                        <Zap className="h-4 w-4 text-primary" />
                        Aprovação Automática (QR do cliente)
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Quando ativado, pedidos feitos pelo celular (via QR Code da mesa) vão direto para produção e imprimem automaticamente.
                        Pedidos do Tablet (modo /tablet) sempre são automáticos.
                      </p>
                    </div>
                    <Switch
                      checked={autoApproveTableOrders}
                      onCheckedChange={setAutoApproveTableOrders}
                    />
                  </div>

                  {autoApproveTableOrders && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Pedidos serão impressos automaticamente nos locais de produção. 
                        Certifique-se de que as impressoras estão configuradas corretamente.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              <Separator />

              <h4 className="font-medium">Opções de Entrega</h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <Label className="text-base flex items-center gap-2">
                      <Truck className="h-4 w-4" />
                      Delivery
                    </Label>
                  </div>
                  <Switch
                    checked={deliveryEnabled}
                    onCheckedChange={setDeliveryEnabled}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <Label className="text-base flex items-center gap-2">
                      <Store className="h-4 w-4" />
                      Retirada
                    </Label>
                  </div>
                  <Switch
                    checked={pickupEnabled}
                    onCheckedChange={setPickupEnabled}
                  />
                </div>
              </div>

              {deliveryEnabled && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Taxa de Entrega (R$)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.50"
                        value={deliveryFee}
                        onChange={(e) => setDeliveryFee(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Pedido Mínimo (R$)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={minOrderAmount}
                        onChange={(e) => setMinOrderAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Tempo Estimado de Entrega</Label>
                    <Input
                      value={deliveryTimeEstimate}
                      onChange={(e) => setDeliveryTimeEstimate(e.target.value)}
                      placeholder="Ex: 30-45 min"
                    />
                  </div>
                </div>
              )}

              {hasPublicMenuChanges && (
                <Button onClick={handleSavePublicMenuSettings} disabled={isUpdatingPublicMenu} className="gap-2">
                  <Save className="h-4 w-4" />
                  {isUpdatingPublicMenu ? "Salvando..." : "Salvar Alterações"}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Delivery Auto-Reply Card */}
          <DeliveryAutoReplyCard />
        </TabsContent>

        {/* Tab QR Codes */}
        <TabsContent value="qrcodes" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                QR Codes das Mesas
              </CardTitle>
              <CardDescription>
                Gere QR Codes para cada mesa. Os clientes podem escanear para ver o cardápio, fazer pedidos e acompanhar a conta.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!publicMenuEnabled ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Ative o Cardápio Digital na aba anterior para gerar QR Codes</p>
                </div>
              ) : activeTables.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <QrCode className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma mesa cadastrada</p>
                  <Button variant="outline" className="mt-4" asChild>
                    <a href="/dashboard/mesas">Cadastrar Mesas</a>
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeTables.map((table) => (
                    <QRCodeGenerator
                      key={table.id}
                      restaurantName={activeRestaurant?.name || ""}
                      restaurantSlug={activeRestaurant?.slug || ""}
                      tableNumber={table.number}
                      logoUrl={activeRestaurant?.logo_url}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Impressão */}
        <TabsContent value="impressao" className="space-y-6 mt-6">
          <PrintTemplateSettings />
          <CancellationPrintSettingsCard />
          <ExitReleasePrintCard />
          <QZTrayConfigCard />
        </TabsContent>

        {/* Tab Garçom Mobile */}
        <TabsContent value="garcom" className="mt-6">
          <PWAInstructions />
        </TabsContent>

        {/* Tab Tablet */}
        <TabsContent value="tablet" className="space-y-6 mt-6">
          <TabletBannersManager />
          <TabletInstallInstructions />
        </TabsContent>

        {/* Tab Mesas & Comandas */}
        <TabsContent value="mesas-comandas" className="space-y-6 mt-6">
          <TableConsumptionSettingsCard />
          <CommandEntryRequirementCard />
          <TableManagementCard />
          <CommandManagementCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
